import { FEATURE_KEYS } from './features.js';
/**
 * The capability matrix from GBV-DEV-2026-001 Section 6, as code.
 *
 * This is the single source both the seeder and the admin UI read from. It is
 * the DEFAULT applied when a Membership is created — an admin can override any
 * individual key afterwards through Membership.featureAccess.
 */
function grant(...keys) {
    return Object.fromEntries(keys.map((k) => [k, true]));
}
function grantAll() {
    return Object.fromEntries(FEATURE_KEYS.map((k) => [k, true]));
}
export const ROLE_FEATURE_DEFAULTS = {
    TELECALLER: grant('crm', 'dataImport', 'dataExport', 'dialer', 'callRecordings', 'whatsappInbox', 'dailyReport', 'assets', 'tasks', 'appointments', 'aiAssistant'),
    SALES_EXEC: grant('crm', 'dataImport', 'dataExport', 'dialer', 'callRecordings', 'whatsappInbox', 'whatsappSend', 'invoicingView', 'dailyReport', 'assets', 'tasks', 'appointments', 'analytics', 'aiAssistant'),
    WA_EXEC: grant('crm', 'dataImport', 'dataExport', 'whatsappInbox', 'whatsappSend', 'dailyReport', 'assets', 'tasks', 'appointments', 'aiAssistant'),
    SOCIAL_EXEC: grant('socialMedia', 'contentCalendar', 'assets', 'dailyReport', 'tasks', 'analytics', 'aiAssistant'),
    DESIGNER: grant('assets', 'contentCalendar', 'tasks', 'aiAssistant'),
    // Back office coordinates and documents rather than sells: it sees the
    // pipeline and raised invoices, but cannot dial out, message clients, or
    // raise an invoice itself.
    OPS_EXEC: grant('crm', 'dataImport', 'dataExport', 'invoicingView', 'dailyReport', 'assets', 'tasks', 'appointments', 'aiAssistant'),
    FINANCE: grant('invoicing', 'invoicingView', 'payments', 'expenses', 'assets', 'confidentialAssets', 'dailyReport', 'tasks', 'aiAssistant'),
    // The only role holding `hr` without holding everything. Deliberately no
    // `crm`: HR has no business in a client pipeline, and the capability
    // matrix is the place to say so rather than trusting people not to look.
    HR_EXEC: grant('hr', 'dailyReport', 'assets', 'tasks', 'appointments', 'aiAssistant'),
    CS_EXEC: grant('crm', 'dataImport', 'dataExport', 'whatsappInbox', 'whatsappSend', 'appointments', 'assets', 'dailyReport', 'tasks', 'aiAssistant'),
    // Team leads supervise but do not raise invoices or manage members.
    TEAM_LEAD: grant('crm', 'dataImport', 'dataExport', 'dialer', 'callRecordings', 'whatsappInbox', 'whatsappSend', 'waBroadcast', 'socialMedia', 'contentCalendar', 'approvals', 'invoicingView', 'dailyReport', 'reportSend', 'assets', 'confidentialAssets', 'tasks', 'appointments', 'analytics', 'auditLog', 'aiAssistant'),
    SUB_ADMIN: grantAll(),
    SUPER_ADMIN: grantAll(),
    // Client-portal roles. Client routes are gated by portal + org membership;
    // these keys drive what the client portal chooses to render.
    // A client owner may take their own data out. It is theirs, and refusing
    // is the kind of thing that makes people distrust a platform. Import is
    // off by default: loading records into the list their agency works from
    // is a real request, but it is the agency's call to allow.
    CLIENT_OWNER: grant('dailyReport', 'approvals', 'invoicingView', 'assets', 'analytics', 'dataExport'),
    /*
     * The manager who runs the account day to day. Everything the owner reads,
     * and the approvals, because approving the week's posts is the job. Not
     * dataExport: taking the whole book out of the platform is the owner's
     * decision, not the account manager's.
     */
    CLIENT_MANAGER: grant('dailyReport', 'approvals', 'invoicingView', 'assets', 'analytics'),
    CLIENT_VIEWER: grant('dailyReport', 'invoicingView', 'assets'),
};
/**
 * Default daily ceilings. 0 means unlimited — a role that should not perform an
 * action at all is stopped by featureAccess, not by a limit of zero.
 */
export const ROLE_LIMIT_DEFAULTS = {
    TELECALLER: { maxCallsPerDay: 80, maxWaPerDay: 30, maxLeadsPerDay: 50, maxAiPerDay: 100 },
    // A higher lead ceiling than a telecaller: generating them is the role.
    SALES_EXEC: {
        maxCallsPerDay: 80,
        maxWaPerDay: 60,
        maxLeadsPerDay: 200,
        maxExportsPerDay: 5,
        maxAiPerDay: 150,
    },
    WA_EXEC: { maxWaPerDay: 150, maxLeadsPerDay: 50, maxAiPerDay: 100 },
    SOCIAL_EXEC: { maxAiPerDay: 150 },
    DESIGNER: { maxAiPerDay: 100 },
    OPS_EXEC: { maxExportsPerDay: 10, maxAiPerDay: 100 },
    FINANCE: { maxInvoicesPerDay: 40, maxExportsPerDay: 10, maxAiPerDay: 100 },
    HR_EXEC: { maxExportsPerDay: 5, maxAiPerDay: 100 },
    CS_EXEC: { maxWaPerDay: 120, maxAiPerDay: 100 },
    TEAM_LEAD: { maxExportsPerDay: 25, maxBroadcastPerDay: 2000, maxAiPerDay: 200 },
    SUB_ADMIN: {},
    SUPER_ADMIN: {},
    CLIENT_OWNER: {},
    CLIENT_MANAGER: {},
    CLIENT_VIEWER: {},
};
export function defaultFeatureAccess(role) {
    return { ...ROLE_FEATURE_DEFAULTS[role] };
}
export function defaultUsageLimits(role) {
    return { ...ROLE_LIMIT_DEFAULTS[role] };
}
/** Human labels for role pickers. */
export const ROLE_LABELS = {
    TELECALLER: 'Telecaller',
    SALES_EXEC: 'Sales & Lead Generation',
    WA_EXEC: 'WhatsApp Executive',
    SOCIAL_EXEC: 'Social Media Marketing',
    DESIGNER: 'Graphic Design',
    OPS_EXEC: 'Back-Office Operations',
    FINANCE: 'Finance & Accounting',
    HR_EXEC: 'HR Support',
    CS_EXEC: 'Customer Success',
    TEAM_LEAD: 'Team Leader',
    SUB_ADMIN: 'Sub Admin',
    SUPER_ADMIN: 'Master Admin',
    CLIENT_OWNER: 'Client Owner / CEO',
    CLIENT_MANAGER: 'Client Manager',
    CLIENT_VIEWER: 'Client Viewer',
};
//# sourceMappingURL=roleDefaults.js.map